import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Switch } from "components/ui/switch";
import { ArrowLeft } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/table";
import { useOrganisation } from "contexts/OrganisationProvider";
import { toast } from "utils/toast";
import { useMemo } from "react";
import {
  type ProductAvailabilityCode,
  ProductAvailabilityCodes,
} from "shared/constants/ProductAvailability";
import { InfoTooltip } from "components/InfoTooltip";
import { Text } from "components/text";
import { getStatusCodeDescription } from "./statusCodeMappings";

export function ManageProductAvailabilityPage() {
  const { t } = useTranslation(["settings", "general"]);
  const {
    id: organisationId,
    organisation,
    updateOrganisationSettings,
  } = useOrganisation();

  const inactiveCodes = useMemo(
    () => organisation?.settings?.inactiveProductStatus || [],
    [organisation?.settings?.inactiveProductStatus],
  );

  const noCodeActive = !organisation?.settings?.excludeNoAvailabilityCode;

  const runUpdate = async (
    body: Parameters<typeof updateOrganisationSettings>[0],
  ) => {
    try {
      await updateOrganisationSettings(body);
      toast.success(t("organisation.productAvailability.updateSuccess"));
    } catch (error) {
      toast.error(
        t("organisation.productAvailability.updateError", {
          message: (error as Error).message,
        }),
      );
    }
  };

  const handleToggleStatus = (code: ProductAvailabilityCode) => {
    const inactiveProductStatus = inactiveCodes.includes(code)
      ? inactiveCodes.filter((c) => c !== code)
      : [...inactiveCodes, code];

    return runUpdate({ inactiveProductStatus });
  };

  const handleToggleNoCode = (checked: boolean) =>
    runUpdate({ excludeNoAvailabilityCode: !checked });

  return (
    <div className="space-y-6">
      <div className="flex items-center text-primary-500">
        <Link
          to="/settings/organisations/$organisationId/settings"
          params={{ organisationId }}
          className="flex items-center gap-2 text-sm"
        >
          <ArrowLeft className="h-4 w-4 " />
          {t("organisation.productAvailability.back")}
        </Link>
      </div>

      <div>
        <Text as="h2" variant="pageHeading" className="mb-2">
          {t("organisation.productAvailability.title")}
        </Text>
        <Text variant="subtle">
          {t("organisation.productAvailability.description")}
        </Text>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("organisation.productAvailability.code")}</TableHead>
            <TableHead>
              {t("organisation.productAvailability.heading")}
            </TableHead>
            <TableHead className="text-right">
              <div className="flex items-center justify-end gap-2">
                {t("organisation.productAvailability.active")}
                <InfoTooltip
                  text={t("organisation.productAvailability.activeTooltip")}
                  iconSize="small"
                  contentClassName="font-normal"
                />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow data-testid="no-availability-code-row">
            <TableCell>-</TableCell>
            <TableCell>
              {t("organisation.productAvailability.noAvailabilityCode")}
            </TableCell>
            <TableCell className="text-right">
              <Switch
                checked={noCodeActive}
                onCheckedChange={handleToggleNoCode}
                data-testid="no-availability-code-switch"
              />
            </TableCell>
          </TableRow>
          {ProductAvailabilityCodes.map((code) => (
            <TableRow key={code}>
              <TableCell>{code}</TableCell>
              <TableCell>{getStatusCodeDescription(code, t)}</TableCell>
              <TableCell className="text-right">
                <Switch
                  checked={!inactiveCodes.includes(code)}
                  onCheckedChange={() => handleToggleStatus(code)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
